'use client';
import { OnbForm } from '@/components/form';
import { useStepData } from '@/components/stepData';
import { connectedField } from '@/redux/fields';
import { RootState } from '@/redux/store';
import { Grid, TextField, Typography } from '@mui/material';
import { merchantContactInformation } from '@repo/onb-api';
import { extractAreaCode, testEmail, validAreaCode } from '@repo/utils';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import StepWrapper from './stepWrapper';

export default function MerchantContactInformation({
  stepdata: stepDataProp,
  stepname: stepName,
}: {
  stepdata?: merchantContactInformation['variables'];
  stepname?: merchantContactInformation['name'];
}): React.JSX.Element {
  const stepData = useStepData(stepDataProp);
  const { steps } = useSelector((state: RootState) => state.flowdata);
  const dispatch = useDispatch();
  const [phoneAreaCode, setPhoneAreaCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  useEffect(() => {
    const extractCode = extractAreaCode(stepData?.mobilePhoneNumber || '');
    if ((stepData?.mobilePhoneNumber?.length || 0) > 0) {
      if (extractCode && !phoneAreaCode && !phoneNumber) {
        setPhoneAreaCode(extractCode);
        setPhoneNumber(stepData?.mobilePhoneNumber?.slice(extractCode.length) || '');
      }
    }
  }, [dispatch, steps, stepData?.mobilePhoneNumber]);

  return (
    <StepWrapper>
      <Typography variant="h1">Datos del representante legal:</Typography>
      <Typography component="p" variant="subtitle2">
        Asegurate de cargar los datos de la persona que tenga la potestad de actuar en nombre de la empresa.
      </Typography>
      <OnbForm<merchantContactInformation> stepName={stepName!}>
        <TextField
          label="Ingresá tu nombre"
          autoFocus
          placeholder="Ej: Juan"
          slotProps={{ htmlInput: { inputMode: 'text', autoComplete: 'given-name', 'data-testid': 'firstName' } }}
          {...connectedField({
            stepName,
            name: 'firstName',
            required: true,
            errorHandler: {
              type: 'text',
              errorText: 'El nombre ingresado no es válido',
              validate: (value) => /^[A-Za-zÁÉÍÓÚáéíóúÑñÜü\s]{1,}$/.test(value),
            },
          })}
        />
        <TextField
          label="Ingresá tu apellido"
          placeholder="Ej: Pérez"
          slotProps={{ htmlInput: { inputMode: 'text', autoComplete: 'family-name', 'data-testid': 'lastName' } }}
          {...connectedField({
            stepName,
            name: 'lastName',
            required: true,
            errorHandler: {
              type: 'text',
              errorText: 'El apellido ingresado no es válido',
              validate: (value) => /^[A-Za-zÁÉÍÓÚáéíóúÑñÜü\s]{1,}$/.test(value),
            },
          })}
        />
        <TextField
          label="Ingresá tu email laboral"
          placeholder="Ej: ejemplo@dominio.com"
          slotProps={{ htmlInput: { inputMode: 'email', autoComplete: 'email', 'data-testid': 'email' } }}
          {...connectedField({
            stepName,
            name: 'email',
            required: true,
            beforeChange: (value) => value?.trim(),
            errorHandler: {
              type: 'text',
              errorText: 'El correo ingresado no es válido',
              validate: (value) => testEmail(value),
            },
          })}
        />
        <Grid
          container
          direction={'row'}
          wrap="nowrap"
          sx={{
            gap: 2,
          }}
        >
          <Grid size={{ xs: 4 }}>
            <TextField
              label="Cód. Área"
              placeholder="Ej: 11 o 348"
              helperText="Sin el 0"
              slotProps={{ htmlInput: { inputMode: 'numeric', 'data-testid': 'phoneAreaCode', maxLength: 4 } }}
              {...connectedField({
                beforeRender: () => phoneAreaCode || '',
                beforeChange: (value) => {
                  setPhoneAreaCode(value?.replaceAll(/\D+/g, ''));
                  return `${value?.replaceAll(/\D+/g, '') || ''}${phoneNumber || ''}`;
                },
                stepName,
                name: 'mobilePhoneNumber',
                required: true,
                errorHandler: {
                  type: 'text',
                  errorText: 'Código inválido',
                  validate: (value) => validAreaCode(value),
                },
              })}
            />
          </Grid>
          <Grid size={{ xs: 8 }}>
            <TextField
              label="Celular"
              placeholder="Ej: 1234567"
              helperText="Sin el 15"
              slotProps={{
                htmlInput: { inputMode: 'numeric', 'data-testid': 'phoneNumber', maxLength: 10 - phoneAreaCode.length },
              }}
              {...connectedField({
                beforeRender: (value) => value?.slice(phoneAreaCode.length) || '',
                beforeChange: (value) => {
                  setPhoneNumber(value?.replaceAll(/\D+/g, ''));
                  return `${phoneAreaCode}${value?.replaceAll(/\D+/g, '')}`;
                },
                stepName,
                name: 'mobilePhoneNumber',
                required: true,
                errorHandler: {
                  type: 'text',
                  errorText: 'Ingresá un celular válido',
                  validate: (value) => value.length + phoneAreaCode.length === 10,
                },
              })}
            />
          </Grid>
        </Grid>
      </OnbForm>
    </StepWrapper>
  );
}
